import { useState, useEffect } from 'react';
import { ArrowLeft, Plus, Link2, Filter, Tag, Folder, ExternalLink, Copy, Trash2, Edit2 } from 'lucide-react';
import { databaseService } from '../../../lib/appwrite';
import { useToast } from '../../../contexts/ToastContext';
import WorkLinkForm from './WorkLinkForm';

const WORK_LINKS_COLLECTION = 'work_links';

export default function WorkLinkManagement({ onBack }) {
  const [workLinks, setWorkLinks] = useState([]);
  const [availableLinks, setAvailableLinks] = useState({
    projects: [],
    spatialCanvas: [],
    certificates: [],
    blogs: []
  });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load categories from different collections
      const [projectCatsRes, mapCatsRes, certCatsRes, blogCatsRes, workLinksRes] = await Promise.all([
        databaseService.listDocuments('categories'),
        databaseService.listDocuments('map_categories'),
        databaseService.listDocuments('certificate_categories'),
        databaseService.listDocuments('blog_categories'),
        databaseService.listDocuments(WORK_LINKS_COLLECTION)
      ]);

      // Transform project categories to links
      const projects = projectCatsRes.success ? projectCatsRes.data.documents
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(cat => ({
          id: cat.$id,
          title: cat.name,
          category: cat.name,
          type: 'projects',
          url: `/projects?category=${encodeURIComponent(cat.name)}`,
          color: cat.color
        })) : [];

      // Transform spatial canvas categories to links
      const spatialCanvas = mapCatsRes.success ? mapCatsRes.data.documents
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(cat => ({
          id: cat.$id,
          title: cat.name,
          category: cat.name,
          type: 'spatial-canvas',
          url: `/spatial-canvas?category=${encodeURIComponent(cat.name)}`,
          color: cat.color
        })) : [];

      // Transform certificate categories to links
      const certificates = certCatsRes.success ? certCatsRes.data.documents
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(cat => ({
          id: cat.$id,
          title: cat.name,
          category: cat.name,
          type: 'certificates',
          url: `/certificates?category=${encodeURIComponent(cat.name)}`,
          color: cat.color
        })) : [];

      // Transform blog categories to links
      const blogs = blogCatsRes.success ? blogCatsRes.data.documents
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(cat => ({
          id: cat.$id,
          title: cat.name,
          category: cat.name,
          type: 'blogs',
          url: `/blogs?category=${encodeURIComponent(cat.name)}`,
          color: cat.color
        })) : [];

      setAvailableLinks({
        projects,
        spatialCanvas,
        certificates,
        blogs
      });

      // Load work links
      if (workLinksRes.success) {
        setWorkLinks(workLinksRes.data.documents);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showToast('Failed to load data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingLink(null);
    setShowForm(true);
  };

  const handleEdit = (link) => {
    setEditingLink(link);
    setShowForm(true);
  };

  const handleDelete = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this work link?')) return;

    try {
      const result = await databaseService.deleteDocument(WORK_LINKS_COLLECTION, linkId);
      if (result.success) {
        showToast('Work link deleted successfully', 'success');
        loadData();
      } else {
        showToast('Failed to delete work link', 'error');
      }
    } catch (error) {
      console.error('Error deleting work link:', error);
      showToast('Error deleting work link', 'error');
    }
  };

  const handleCopy = (url) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl);
    showToast('URL copied to clipboard', 'success');
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingLink) {
        await databaseService.updateDocument(WORK_LINKS_COLLECTION, editingLink.$id, formData);
        showToast('Work link updated successfully', 'success');
      } else {
        await databaseService.createDocument(WORK_LINKS_COLLECTION, formData);
        showToast('Work link created successfully', 'success');
      }
      setShowForm(false);
      setEditingLink(null);
      loadData();
    } catch (error) {
      console.error('Error saving work link:', error);
      showToast('Failed to save work link', 'error');
      throw error;
    }
  };

  if (showForm) {
    return (
      <WorkLinkForm
        link={editingLink}
        availableLinks={availableLinks}
        onSubmit={handleFormSubmit}
        onCancel={() => {
          setShowForm(false);
          setEditingLink(null);
        }}
      />
    );
  }

  const renderAvailableLinks = (type, links, icon) => {
    const isExpanded = expandedSection === type;
    
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <button
          onClick={() => setExpandedSection(isExpanded ? null : type)}
          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center gap-3">
            {icon}
            <div className="text-left">
              <h3 className="font-semibold text-gray-900 capitalize">{type.replace('-', ' ')}</h3>
              <p className="text-sm text-gray-500">{links.length} categories available</p>
            </div>
          </div>
          <div className="text-gray-400">
            {isExpanded ? '−' : '+'}
          </div>
        </button>

        {isExpanded && (
          <div className="border-t p-4 bg-gray-50">
            <p className="text-xs text-gray-600 mb-3 italic">
              These are category-filtered links. Click copy to get the URL.
            </p>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {links.map((link) => (
                <div
                  key={link.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border hover:border-[#4C1D95] transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{link.title}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span 
                        className="text-xs px-2 py-0.5 text-white rounded font-medium"
                        style={{ backgroundColor: link.color || '#4C1D95' }}
                      >
                        Category: {link.category}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1 font-mono truncate">{link.url}</p>
                  </div>
                  <button
                    onClick={() => handleCopy(link.url)}
                    className="p-2 text-gray-600 hover:text-[#4C1D95] hover:bg-gray-100 rounded-lg transition-colors"
                    title="Copy URL"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" style={{ color: '#4C1D95' }} />
          </button>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#4C1D95' }}>
              Work Link Management
            </h1>
            <p className="text-gray-600 mt-1">
              View category links and create custom filtered URLs with tags
            </p>
          </div>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-white transition-all duration-300 hover:scale-105"
          style={{ backgroundColor: '#4C1D95' }}
        >
          <Plus className="w-5 h-5" />
          Create Work Link
        </button>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto" style={{ borderColor: '#4C1D95' }}></div>
          <p className="text-gray-600 mt-4">Loading...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column: Available Links */}
          <div>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#4C1D95' }}>
              Available Category Links
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Pre-built category filter links for each page. Copy and share these URLs to show content filtered by category.
            </p>
            <div className="space-y-4">
              {renderAvailableLinks('projects', availableLinks.projects, <Folder className="w-5 h-5" style={{ color: '#4C1D95' }} />)}
              {renderAvailableLinks('spatial-canvas', availableLinks.spatialCanvas, <Folder className="w-5 h-5" style={{ color: '#4C1D95' }} />)}
              {renderAvailableLinks('certificates', availableLinks.certificates, <Folder className="w-5 h-5" style={{ color: '#4C1D95' }} />)}
              {renderAvailableLinks('blogs', availableLinks.blogs, <Folder className="w-5 h-5" style={{ color: '#4C1D95' }} />)}
            </div>
          </div>

          {/* Right Column: Created Work Links */}
          <div>
            <h2 className="text-xl font-bold mb-4" style={{ color: '#4C1D95' }}>
              Created Work Links
            </h2>
            
            {workLinks.length === 0 ? (
              <div className="bg-white rounded-lg shadow-lg p-12 text-center">
                <Link2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">
                  No work links yet
                </h3>
                <p className="text-gray-600 mb-6">
                  Create your first filtered work link
                </p>
                <button
                  onClick={handleCreate}
                  className="px-6 py-3 rounded-lg text-white transition-all duration-300 hover:scale-105"
                  style={{ backgroundColor: '#4C1D95' }}
                >
                  Create Work Link
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {workLinks.map((link) => (
                  <div
                    key={link.$id}
                    className="bg-white rounded-lg shadow-md p-4 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 mb-1">{link.name}</h3>
                        <p className="text-sm text-gray-600 mb-2">{link.description}</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded">
                            {link.pageType}
                          </span>
                          {link.category && (
                            <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded flex items-center gap-1">
                              <Folder className="w-3 h-3" />
                              {link.category}
                            </span>
                          )}
                          {link.subcategory && (
                            <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded flex items-center gap-1">
                              <Folder className="w-3 h-3" />
                              {link.subcategory}
                            </span>
                          )}
                          {link.tags && link.tags.length > 0 && (
                            <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded flex items-center gap-1">
                              <Tag className="w-3 h-3" />
                              {link.tags.length} tags
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm font-mono text-gray-700 mb-3">
                      <code className="flex-1 truncate">{link.generatedUrl}</code>
                    </div>

                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleCopy(link.generatedUrl)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Copy URL"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(link.generatedUrl, '_blank')}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title="Open Link"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(link)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(link.$id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

